import { Autocomplete, Box, Button, CircularProgress, IconButton, Modal, TextField, Typography, Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import styled from "@emotion/styled";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ContentBox from "../../../components/ui/ContentBox";
import ButtonSquare from "../../../components/ui/ButtonSquare";
import Api from "../../../api/Api";
import { Urls } from "../../../api/Urls";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { ToastContainer, toast } from "react-toastify";
import { modalStyleOverflow } from "../../../theme/ModalStyle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
  { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
  { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
];

const STATUS_COLORS = {
  draft: { bg: "#FFF3E0", color: "#E65100" },
  approved: { bg: "#E3F2FD", color: "#1565C0" },
  paid: { bg: "#E8F5E9", color: "#2E7D32" },
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "white",
    paddingTop: "8px",
    paddingBottom: "8px",
    letterSpacing: "1px",
    color: "#4B465C",
    fontSize: 13,
    fontWeight: 500,
  },
  [`&.${tableCellClasses.body}`]: {
    background: "white",
    fontSize: 14,
    color: "#666D83",
    fontWeight: "500",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-child td, &:last-child th": { border: 0 },
  ":hover": { filter: "brightness(0.95)" },
  cursor: "pointer",
}));

const StyledTable = styled(Table)({ borderRadius: "0px" });

function Payslips({ id }) {
  const { user } = useAuthContext();
  const access_token = user?.access_token;

  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({ month: null, year: null });
  const [openBreakdown, setOpenBreakdown] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [change, setChange] = useState(false);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(null);
  const [generateYear, setGenerateYear] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deletePayslip, setDeletePayslip] = useState(null);

  useEffect(() => {
    document.title = "Moody admin - Payslips";
  }, []);

  useEffect(() => {
    setLoading(true);
    Api.get(Urls.GET_COACH_PAYSLIPS, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
      params: {
        coach_id: id,
        ...(filter.month ? { month: filter.month } : {}),
        ...(filter.year ? { year: filter.year } : {}),
      },
    })
      .then((res) => {
        setPayslips(res?.data?.data || []);
        setData(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.response?.data?.message || "Error loading payslips");
      });
  }, [filter, change]);

  const handlePagination = (link) => {
    Api.get(link, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
    })
      .then((res) => {
        setData(res?.data);
        setPayslips(res?.data?.data || []);
      })
      .catch(() => setPayslips([]));
  };

  const handleStatusChange = (payslipId, newStatus) => {
    Api.patch(
      Urls.UPDATE_COACH_PAYSLIP_STATUS + payslipId + "/status",
      { status: newStatus },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
    )
      .then(() => {
        toast.success(`Payslip marked as ${newStatus}`);
        setChange(!change);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error updating status");
      });
  };

  const getMonthName = (m) => MONTHS.find((mo) => mo.value === m)?.label || m;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleGenerate = () => {
    if (!generateMonth || !generateYear) {
      toast.error("Please select month and year");
      return;
    }
    setGenerating(true);
    Api.post(
      Urls.GENERATE_COACH_PAYSLIPS,
      { month: generateMonth, year: generateYear },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
    )
      .then((res) => {
        toast.success(res?.data?.message || "Payslip generated successfully");
        setOpenGenerate(false);
        setGenerating(false);
        setChange(!change);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error generating payslip");
        setGenerating(false);
      });
  };

  const handleExportPdf = (payslipId) => {
    Api.get(Urls.EXPORT_COACH_PAYSLIP_PDF + payslipId + "/export-pdf", {
      headers: { Authorization: `Bearer ${access_token}` },
      responseType: "blob",
    })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = res.headers["content-disposition"];
        let filename = `payslip_${payslipId}.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)/i);
          if (match && match[2]) filename = match[2];
        }
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("PDF downloaded successfully");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error exporting PDF");
      });
  };

  const handleDelete = () => {
    Api.delete(Urls.DELETE_COACH_PAYSLIP + deletePayslip.id, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Payslip deleted successfully");
        setOpenDelete(false);
        setDeletePayslip(null);
        setChange(!change);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error deleting payslip");
      });
  };

  return (
    <Box display="flex" flexDirection="column" gap="26px">
      <ToastContainer />
      <Box>
        <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
          Payslips
        </Typography>
      </Box>

      <ContentBox>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="16px">
            <Typography fontSize="18px" fontWeight="500" lineHeight="24px" color="#4B465C">
              Payslip History
            </Typography>
            <Box display="flex" gap="8px" alignItems="center">
              <Button variant="primary_active" size="small" onClick={() => setOpenGenerate(true)}>
                Generate Payslip
              </Button>
              <Autocomplete
                options={MONTHS}
                getOptionLabel={(o) => o.label}
                value={MONTHS.find((m) => m.value === filter.month) || null}
                onChange={(_, v) => setFilter({ ...filter, month: v?.value || null })}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Month" sx={{ width: 150 }} />}
              />
              <Autocomplete
                options={yearOptions}
                getOptionLabel={(o) => String(o)}
                value={filter.year || null}
                onChange={(_, v) => setFilter({ ...filter, year: v || null })}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Year" sx={{ width: 120 }} />}
              />
              {(filter.month || filter.year) && (
                <Button variant="primary_inactive" size="small" onClick={() => setFilter({ month: null, year: null })}>
                  Clear
                </Button>
              )}
            </Box>
          </Box>

          {loading && (
            <Box display="flex" justifyContent="center"><CircularProgress /></Box>
          )}

          {!loading && (
            <TableContainer sx={{ borderRadius: "0px", border: "1px solid #DBDADE" }}>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>PERIOD</StyledTableCell>
                    <StyledTableCell align="center">SESSIONS</StyledTableCell>
                    <StyledTableCell align="center">HOURS</StyledTableCell>
                    <StyledTableCell align="center">PLAYERS</StyledTableCell>
                    <StyledTableCell align="center">REVENUE</StyledTableCell>
                    <StyledTableCell align="center">SALARY</StyledTableCell>
                    <StyledTableCell align="center">STATUS</StyledTableCell>
                    <StyledTableCell align="center">ACTIONS</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payslips.map((p) => (
                    <StyledTableRow
                      key={p.id}
                      onClick={() => { setSelectedPayslip(p); setOpenBreakdown(true); }}
                    >
                      <StyledTableCell>
                        {getMonthName(p.month)} {p.year}
                      </StyledTableCell>
                      <StyledTableCell align="center">{p.total_sessions}</StyledTableCell>
                      <StyledTableCell align="center">{p.total_hours}</StyledTableCell>
                      <StyledTableCell align="center">{p.total_players}</StyledTableCell>
                      <StyledTableCell align="center">{p.total_revenue} BHD</StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography fontWeight="600" color="#4B465C">{p.calculated_salary} BHD</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip
                          label={p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: STATUS_COLORS[p.status]?.bg || "#F5F5F5",
                            color: STATUS_COLORS[p.status]?.color || "#666",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" justifyContent="center" gap="4px">
                          {p.status === "draft" && (
                            <Button
                              size="small"
                              variant="primary_active"
                              sx={{ fontSize: "11px", padding: "2px 8px", minWidth: "auto" }}
                              onClick={() => handleStatusChange(p.id, "approved")}
                            >
                              Approve
                            </Button>
                          )}
                          {p.status === "approved" && (
                            <Button
                              size="small"
                              variant="primary_active"
                              sx={{ fontSize: "11px", padding: "2px 8px", minWidth: "auto" }}
                              onClick={() => handleStatusChange(p.id, "paid")}
                            >
                              Mark Paid
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleExportPdf(p.id)}
                            title="Export PDF"
                            sx={{ color: "#4B465C" }}
                          >
                            <FileDownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                          <ButtonSquare
                            action="delete_red"
                            onClick={() => { setDeletePayslip(p); setOpenDelete(true); }}
                          />
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          )}

          {!loading && payslips.length === 0 && (
            <Typography textAlign="center" color="#666D83">No payslips found</Typography>
          )}

          {payslips.length > 0 && (
            <Box justifyContent="flex-end" display="flex" gap="4px">
              {data?.links?.prev && (
                <ButtonSquare onClick={() => { handlePagination(data?.links?.prev); window.scrollTo({ top: 0, behavior: "smooth" }); }} action="Previous" />
              )}
              <Box display="flex" gap="4px" justifyContent="center">
                {data?.meta?.links?.map((link, i) => (
                  <ButtonSquare
                    key={i}
                    onClick={() => { handlePagination(link.url); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    action={link?.active ? "number-active" : "number"}
                    number={link.label}
                  />
                ))}
              </Box>
              {data?.links?.next && (
                <ButtonSquare onClick={() => { handlePagination(data?.links?.next); window.scrollTo({ top: 0, behavior: "smooth" }); }} action="Next" />
              )}
            </Box>
          )}
        </Box>
      </ContentBox>

      {/* Breakdown Modal */}
      <Modal open={openBreakdown} onClose={() => { setOpenBreakdown(false); setSelectedPayslip(null); }}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="4px">
            Payslip Breakdown
          </Typography>
          {selectedPayslip && (
            <>
              <Typography fontSize="14px" color="#6F6B7D" mb="16px">
                {getMonthName(selectedPayslip.month)} {selectedPayslip.year}
              </Typography>

              <Box display="flex" gap="16px" mb="16px" flexWrap="wrap">
                <Box sx={{ p: 2, borderRadius: "6px", border: "1px solid #DBDADE", flex: 1, minWidth: 100 }}>
                  <Typography fontSize="12px" color="#A8AAAE">Sessions</Typography>
                  <Typography fontSize="18px" fontWeight="600" color="#4B465C">{selectedPayslip.total_sessions}</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: "6px", border: "1px solid #DBDADE", flex: 1, minWidth: 100 }}>
                  <Typography fontSize="12px" color="#A8AAAE">Hours</Typography>
                  <Typography fontSize="18px" fontWeight="600" color="#4B465C">{selectedPayslip.total_hours}</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: "6px", border: "1px solid #DBDADE", flex: 1, minWidth: 100 }}>
                  <Typography fontSize="12px" color="#A8AAAE">Players</Typography>
                  <Typography fontSize="18px" fontWeight="600" color="#4B465C">{selectedPayslip.total_players}</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: "6px", border: "1px solid #DBDADE", flex: 1, minWidth: 100 }}>
                  <Typography fontSize="12px" color="#A8AAAE">Revenue</Typography>
                  <Typography fontSize="18px" fontWeight="600" color="#4B465C">{selectedPayslip.total_revenue} BHD</Typography>
                </Box>
              </Box>

              <TableContainer sx={{ borderRadius: "0px", border: "1px solid #DBDADE", mb: "16px" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#4B465C" }}>Component</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: "#4B465C" }}>Rate</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: "#4B465C" }}>Metric</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#4B465C" }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPayslip.breakdown?.components?.map((comp, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ color: "#666D83" }}>{comp.component_name}</TableCell>
                        <TableCell align="center" sx={{ color: "#666D83" }}>
                          {comp.type === "revenue_percentage" ? `${comp.rate}%` : `${comp.rate} BHD`}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#666D83" }}>
                          {comp.type === "fixed_monthly" && "—"}
                          {comp.type === "per_hour" && `${comp.hours} hrs`}
                          {comp.type === "per_session" && `${comp.sessions} sessions`}
                          {comp.type === "per_player" && `${comp.players} players`}
                          {comp.type === "revenue_percentage" && `${comp.revenue} BHD`}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: "#4B465C" }}>
                          {comp.amount} BHD
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700, color: "#4B465C", fontSize: 15 }}>
                        Total Salary
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#FF0101", fontSize: 15 }}>
                        {selectedPayslip.breakdown?.total || selectedPayslip.calculated_salary} BHD
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display="flex" justifyContent="flex-end">
                <Button variant="primary_inactive" onClick={() => { setOpenBreakdown(false); setSelectedPayslip(null); }}>
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {/* Generate Payslip Modal */}
      <Modal open={openGenerate} onClose={() => setOpenGenerate(false)}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="8px">
            Generate Payslip
          </Typography>
          <Typography fontSize="14px" color="#666D83" mb="16px">
            Generate payslip for this coach for the selected period.
          </Typography>
          <Box display="flex" gap="16px" mb="16px">
            <Autocomplete
              options={MONTHS}
              getOptionLabel={(o) => o.label}
              value={MONTHS.find((m) => m.value === generateMonth) || null}
              onChange={(_, v) => setGenerateMonth(v?.value || null)}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Month" />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={yearOptions}
              getOptionLabel={(o) => String(o)}
              value={generateYear || null}
              onChange={(_, v) => setGenerateYear(v || null)}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Year" />}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap="8px">
            <Button variant="primary_inactive" onClick={() => setOpenGenerate(false)}>Cancel</Button>
            <Button variant="primary_active" onClick={handleGenerate} disabled={generating}>
              {generating ? <CircularProgress size={20} /> : "Generate"}
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal open={openDelete} onClose={() => { setOpenDelete(false); setDeletePayslip(null); }}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="8px">
            Delete Payslip
          </Typography>
          <Typography fontSize="14px" color="#666D83" mb="16px">
            Are you sure you want to delete the payslip for{" "}
            {deletePayslip && `${getMonthName(deletePayslip.month)} ${deletePayslip.year}`}?
            This action cannot be undone.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap="8px">
            <Button variant="primary_inactive" onClick={() => { setOpenDelete(false); setDeletePayslip(null); }}>Cancel</Button>
            <Button
              variant="primary_active"
              sx={{ backgroundColor: "#FF0101" }}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default Payslips;
